import { useMemo } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import { traverse } from '@atlaskit/adf-utils/traverse';
import { expValEquals } from '@confluence/feature-experiments';

import { traverseWithNestedContent } from './traverseWithNestedContent.ts';

import {
	type ContentExtensionMetrics,
	retrieveContentExtensionMetricsFromNode,
} from '@confluence/view-content-perf-metrics/entry-points/extensionAnalyticsHelpers.ts';

export type AdfStats = {
	contentExtensionMetrics: ContentExtensionMetrics;
	nodeCountByType: Record<string, number>;
	totalNodeCount: number;
};

type CustomPerfData = { contentExtensionMetrics?: ContentExtensionMetrics } | null | undefined;

export function useAdfStats(adfDocument: DocNode, customPerfData?: CustomPerfData): AdfStats {
	const shouldRetrieveContentExtensionMetrics = Boolean(customPerfData);

	const adfStats = useMemo(() => {
		const contentExtensionMetrics: ContentExtensionMetrics = {
			contentExtensionCount: {
				connect: {},
				forge: {},
			},
			contentExtensionKeys: {
				connect: [],
				forge: [],
			},
			hasExtensions: false,
		};
		const nodeCountByType: Record<string, number> = {};
		let totalNodeCount = 0;

		if (expValEquals('confluence_instrument_lcm_nodes', 'isEnabled', true)) {
			traverseWithNestedContent(adfDocument, (node) => {
				nodeCountByType[node.type] = (nodeCountByType[node.type] || 0) + 1;
				totalNodeCount++;

				if (shouldRetrieveContentExtensionMetrics) {
					retrieveContentExtensionMetricsFromNode(node, contentExtensionMetrics);
				}
			});
		} else {
			traverse(adfDocument, {
				any: (node) => {
					nodeCountByType[node.type] = (nodeCountByType[node.type] || 0) + 1;
					totalNodeCount++;

					// This is the alternative to `retrieveContentExtensionMetrics` which avoids one more parsing of the ADF string into JSON.
					// Note: Unlike the old `retrieveContentExtensionMetrics` which only considers the top-level content of the document,
					// this considers the whole document.
					if (shouldRetrieveContentExtensionMetrics) {
						retrieveContentExtensionMetricsFromNode(node, contentExtensionMetrics);
					}
				},
			});
		}

		return {
			contentExtensionMetrics,
			nodeCountByType,
			totalNodeCount,
		};
	}, [adfDocument, shouldRetrieveContentExtensionMetrics]);

	if (shouldRetrieveContentExtensionMetrics) {
		customPerfData!.contentExtensionMetrics = adfStats.contentExtensionMetrics;
	}

	return adfStats;
}
